/*
 * Product card colour swatches — the dots under a card's price
 * (snippets/product-card.liquid, rendered where a section passes swatches).
 *
 * The dots are native radios, so choosing one and moving between them with
 * the keyboard already work without this script. What it adds is meaning:
 * when a colour is chosen, the card's photo becomes that colour's image (if
 * the variant has one in the admin — a colour without one shows the product's
 * own photo again), the card's link and the quick-add link point at that
 * variant, and the quick-add button adds it — or hides while that colour is
 * sold out.
 *
 * The new photo is decoded off-screen before it replaces the old one, so the
 * frame never goes blank in between — but only for a moment: past that the
 * swap goes ahead regardless (a background tab may never report a decode),
 * and the browser keeps the old photo up until the new one arrives anyway.
 * If the visitor clicks on before a swap lands, the later choice wins.
 *
 * One delegated listener serves every card, including cards appended later by
 * load more or re-rendered by the filters. ES modules run once per URL, but
 * the flag keeps a second copy inert.
 */

const PHOTO_ATTRIBUTES = ['src', 'srcset', 'width', 'height', 'alt'];
const DECODE_WAIT = 1500; // ms to give the decode before swapping regardless

/* Per card: the photo it was rendered with, and the latest swap requested
   (so a slow decode can't land after a quicker later choice). */
const original = new WeakMap();
const pending = new WeakMap();

const swapPhoto = async (card, data) => {
  const img = card.querySelector('img.product-card__image');
  if (!img) return;

  if (!original.has(card)) {
    const kept = {};
    PHOTO_ATTRIBUTES.forEach((name) => {
      kept[name] = img.getAttribute(name);
    });
    original.set(card, kept);
  }
  const next = data.src ? data : original.get(card);
  if (!next.src || next.src === img.getAttribute('src')) return;

  const token = (pending.get(card) || 0) + 1;
  pending.set(card, token);

  const probe = new Image();
  probe.sizes = img.sizes;
  probe.srcset = next.srcset || '';
  probe.src = next.src;
  await Promise.race([
    probe.decode().catch(() => {}), // undecodable: swap anyway, the browser shows what it can
    new Promise((resolve) => {
      window.setTimeout(resolve, DECODE_WAIT);
    }),
  ]);
  if (pending.get(card) !== token) return;

  PHOTO_ATTRIBUTES.forEach((name) => {
    if (next[name] == null) img.removeAttribute(name);
    else img.setAttribute(name, next[name]);
  });
};

const select = (card, input) => {
  const data = input.dataset;
  const colour = input.value;

  if (data.variantUrl) {
    card.querySelectorAll('a.product-card__link, a.product-card__quick').forEach((link) => {
      link.href = data.variantUrl;
    });
  }

  const button = card.querySelector('button[data-quick-add]');
  if (button) {
    button.dataset.variantId = data.variantId;
    button.dataset.variantTitle = colour;
    button.setAttribute('aria-label', `Add ${button.dataset.productTitle}, ${colour} to cart`);
    button.hidden = data.available !== 'true';
  }

  swapPhoto(card, data);
};

if (!window.__productCardSwatches) {
  window.__productCardSwatches = true;

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-swatch-input') || !input.checked) return;
    const card = input.closest('.product-card');
    if (card) select(card, input);
  });
}
